import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  ignoreElements,
  map,
  merge,
  shareReplay,
  tap,
  type Observable,
} from 'rxjs';
import { ViewIds } from '@ardoq/api-types';
import { dispatchAction } from '@ardoq/rxbeach';
import { shallowEqual } from 'utils/objectUtils';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { formattingAndLabelRules$ } from 'streams/filters/formattingAndLabelRules$';
import { groupingRules$ } from 'streams/grouping/groupingRules$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import type { ViewpointModeInstanceViewSettings } from 'views/environmentals/viewSettings/types';
import { warmConditionalFormattingFilterCache } from 'tabview/bubbleChart/sourceData/buildViewpointModeViewModelSourceData';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import { updateViewEmptyStateData } from 'appLayout/ardoqStudio/emptyState/actions';
import {
  buildNetwork3DSourceData,
  type Network3DSourceData,
} from '../sourceData/buildNetwork3DSourceData';
import type { Network3DAppViewSettings } from '../types';
import { network3dSearch$ } from '../network3dSearch$';
import {
  toNetwork3DPresentation,
  type Network3DPresentationModel,
} from './toNetwork3DPresentation';

/**
 * What the view is handed, which is the presentation plus the one thing that is not derived from
 * the data: the question the reader is asking of it.
 *
 * Declared here rather than beside `toNetwork3DPresentation`, because this is where the two halves
 * are put together - that module answers for the data and the settings and knows nothing about a
 * search.
 */
export type Network3DViewModel = Network3DPresentationModel & {
  /** What the settings-bar field holds, which is not a setting - see `network3dSearch$`. */
  searchQuery: string;
};

/**
 * The part of the settings that can change the data.
 *
 * These three and no others. Every other setting changes how the graph is laid out and drawn,
 * which the view does downstream - so a derivation keyed on the whole settings object rebuilt
 * every node and edge when a reader switched layout.
 *
 * Size is here because it arrives as `NetworkNode.weight`, which is data rather than styling: the
 * grouped layout reserves each mark's space from it, so it has to be settled before the layout
 * runs. Collapse is here for the stronger version of the same reason - a closed group is a
 * smaller graph, not a differently drawn one.
 *
 * Narrowed and then held still by a `distinctUntilChanged`, because `map` hands over a fresh
 * object per emission and a derivation combined against that re-runs on every settings write. The
 * full settings still reach the view on their own branch below.
 */
const toDataSelection = ({
  nodeSizeBy,
  sizeByFieldName,
  collapsedGroupIds,
}: Network3DAppViewSettings) => ({
  nodeSizeBy,
  sizeByFieldName,
  collapsedGroupIds,
});

/**
 * Notably without `context$`.
 *
 * The XYZ charts narrow it to `workspaceIds` and `componentId` - the first to scope their field
 * list, the second to emphasise the context component. This view reads neither. Nothing in it is
 * drawn differently for being the context, and its field list is scoped off the loaded scope data
 * instead: `numericFieldsInScope` derives the workspaces from the components in scope, so there is
 * no second source to agree with. Subscribing anyway would rebuild the whole graph on every
 * `notifyContextChanged`, which includes selecting a reference and reordering the workspaces.
 */
const NETWORK_3D_DERIVATION_INPUTS = {
  loadedGraph: loadedGraph$,
  locale: locale$,
  i18nLocale: i18nLocale$,
  formattingAndLabelRules: formattingAndLabelRules$,
  /* Only does anything when there is a rule - `buildNetwork3DSourceData` gates on that rather than
     paying for the grouped graph on every rebuild of an ungrouped viewpoint. */
  groupingRules: groupingRules$.pipe(map(({ groupingRules }) => groupingRules)),
  /* For resolving a chosen size field against what this reader may see. A confidential field is
     offered and disabled rather than hidden, so the permission view is part of the answer. */
  permissionContext: currentUserPermissionContext$,
};

/**
 * The two dispatches this view makes off its own source data, as a stream that emits nothing.
 *
 * Merged into the view model rather than run from effects in the view, so the component stays a
 * function of its props and the subscription still follows its lifetime. `ignoreElements` keeps
 * them out of the props: they are subscriptions living on this stream, not values it carries.
 */
const toViewEffects$ = (
  sourceData$: Observable<Network3DSourceData>
): Observable<never> =>
  merge(
    /* What "Create scenario from components in view" is built from. `viewComponentIds$` answers
       `[]` for any view that never dispatches this, and `createFromViewEnabled` requires a
       non-empty list - so without it the menu entry is offered and permanently greyed out.

       The builder's own list, not the marks': a node id is a *mark* id, and grouped those are
       group ids and clone ids rather than component ids. */
    sourceData$.pipe(
      tap(({ componentIds }) =>
        dispatchAction(
          readViewComponentIds({ [ViewIds.NETWORK_3D]: componentIds })
        )
      )
    ),
    /* The studio's own empty state, for the one reason it can answer better than this view can.
       On the verdict rather than the whole source data, so a rebuild reaching the same answer
       does not dispatch again. */
    sourceData$.pipe(
      map(({ emptyReason }) => emptyReason === 'no components'),
      distinctUntilChanged(),
      tap(isViewEmpty =>
        dispatchAction(
          updateViewEmptyStateData({
            viewEmptyStateData: { isViewEmpty },
            viewId: ViewIds.NETWORK_3D,
          })
        )
      )
    )
  ).pipe(ignoreElements());

/** Everything the 3D network view is rendered from. */
export const network3dViewModel$ = (
  viewInstanceId: string,
  viewSettingsWithChanges$: Observable<
    ViewpointModeInstanceViewSettings<ViewIds.NETWORK_3D>
  >
): Observable<Network3DViewModel> => {
  /* The settings stream carries the current settings alongside the keys that just changed; only
     the settings themselves are wanted here. */
  const viewSettings$ = viewSettingsWithChanges$.pipe(
    map(({ currentSettings }) => currentSettings)
  );

  const sourceData$ = combineLatest({
    ...NETWORK_3D_DERIVATION_INPUTS,
    dataSettings: viewSettings$.pipe(
      map(toDataSelection),
      distinctUntilChanged(shallowEqual)
    ),
  }).pipe(
    debounceTime(DEFAULT_DEBOUNCE_TIME),
    distinctUntilChanged(shallowEqual),
    map(({ dataSettings, ...inputs }) =>
      buildNetwork3DSourceData(viewInstanceId, {
        ...inputs,
        ...dataSettings,
        /* Warmed in the same step that reads it rather than as its own stream entry.
           `filterCache` is a mutable singleton, and the debounce above is a window in which
           another view could warm it with its own data. */
        filterColorData: warmConditionalFormattingFilterCache(
          inputs.loadedGraph
        ),
      })
    ),
    /* Three subscribers - the props branch and the two effects - and the derivation rebuilds every
       node and edge. Without this it runs once per subscriber. */
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /* Its own branch, beside the presentation rather than inside it. A search changes which marks
     recede and which rows the list shows - nothing about the graph or the arrangement - so putting
     it through `toDataSelection` would rebuild every node and edge on each keystroke. */
  const searchQuery$ = network3dSearch$.pipe(
    map(({ query }) => query),
    distinctUntilChanged()
  );

  return merge(
    combineLatest([sourceData$, viewSettings$, searchQuery$]).pipe(
      map(([sourceData, viewSettings, searchQuery]) => ({
        ...toNetwork3DPresentation(sourceData, viewSettings),
        searchQuery,
      }))
    ),
    toViewEffects$(sourceData$)
  );
};
